import moment from "moment";
import {
  MAX_NAMED_DONUT_SLICES,
  OTHER_CHART_SLICE_KEY,
  OTHER_CHART_SLICE_NAME,
  buildLabOpsDateRangeFromPeriod,
  parseChartSeriesNumericId,
  resolveTestCategoryName,
  toOptionalIntegerId,
  toOptionalIntegerIds,
} from "./overdueUtils";
import { buildDataOpsExportFileName, downloadDataOpsExcel } from "./utils";

/** Aggregate options for Retest reason trend — client-side rollup only. */
export const RETEST_AGGREGATE_OPTIONS = [
  { label: "Daily", value: "day" },
  { label: "Weekly", value: "week" },
  { label: "Monthly", value: "month" },
];

export const DEFAULT_RETEST_AGGREGATE = "day";

/** Max named reason lines before remaining reasons roll into "Other". */
const MAX_NAMED_REASON_LINES = MAX_NAMED_DONUT_SLICES;

/** Chart drill-down sources opened from RetestCharts. */
export const RETEST_DRILL_DOWN_KIND = {
  REASON_PERIOD: "reason_period",
  CUSTOMER: "customer",
  CATEGORY: "category",
};

const UNKNOWN_CHART_LABEL = "Unknown";

/** Known created_at shapes from retest_traceability (FE guide + ISO + export). */
const RETEST_CREATED_AT_FORMATS = [
  "YYYY-MM-DD hh:mm:ss A",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD",
  "MM/DD/YY hh:mm A",
  "MM/DD/YYYY hh:mm A",
  moment.ISO_8601,
];

/**
 * Build retest_traceability query object from Retest filter UI state.
 * Integer PK fields are arrays (multi-select → repeated query params).
 * @param {object} filters
 * @param {Function} formatDateRange — typically formatAntdDateRange
 * @returns {object|null} null when date range is invalid
 */
export const buildRetestTraceabilityParams = (filters, formatDateRange) => {
  const dateRange = formatDateRange(filters.dateRange);
  if (!dateRange) {
    return null;
  }

  return {
    date_range: dateRange,
    test_category_id: toOptionalIntegerIds(filters.testCategoryId),
    test_id: toOptionalIntegerIds(filters.testId),
    customer_pk: toOptionalIntegerId(filters.customerId),
    search: filters.search || "",
    created_by: toOptionalIntegerId(filters.createdBy),
    subscription_only: Boolean(filters.subscriptionOnly),
  };
};

/**
 * Normalize retest_traceability result into table rows with stable keys.
 * One API row = one RT test (already flat from BE).
 * @param {object[]} [result]
 * @returns {object[]}
 */
export const normalizeRetestTraceabilityRows = (result = []) => {
  const safeRows = Array.isArray(result) ? result : [];

  return safeRows.map((row, index) => ({
    ...row,
    rowKey: [
      row.retest_workorder_id,
      row.original_wo_gbid,
      row.customer_id,
      row.test_name,
      row.sample_type,
      row.sample_id,
      row.test_category_id,
      index,
    ].filter((part) => part != null && part !== "").join("-") || `retest-row-${index}`,
  }));
};

/**
 * Normalize a chart axis / legend label from an optional API string.
 * @param {*} value
 * @returns {string}
 */
const toChartLabel = (value) => {
  if (value == null || String(value).trim() === "") {
    return UNKNOWN_CHART_LABEL;
  }
  return String(value).trim();
};

/**
 * Stable bucket key: prefer numeric/string id, fall back to label.
 * @param {*} idValue
 * @param {string} label
 * @returns {string}
 */
const buildChartSeriesKey = (idValue, label) => {
  if (idValue != null && idValue !== "") {
    return `id:${String(idValue)}`;
  }
  return `label:${label}`;
};

/**
 * Parse RT WO id prefix MMDDYY (e.g. 070726_6001 → 2026-07-07).
 * @param {*} workorderId
 * @returns {moment.Moment|null}
 */
const parseRetestWorkorderDate = (workorderId) => {
  const prefix = String(workorderId ?? "").trim().slice(0, 6);
  if (!/^\d{6}$/.test(prefix)) {
    return null;
  }
  const parsedDate = moment(prefix, "MMDDYY", true);
  return parsedDate.isValid() ? parsedDate.startOf("day") : null;
};

/**
 * Resolve the chart date for a retest row.
 * Prefer created_at (export); list API falls back to retest_workorder_id MMDDYY prefix.
 * @param {object} row
 * @returns {moment.Moment|null}
 */
export const resolveRetestRowDate = (row = {}) => {
  if (row.created_at != null && String(row.created_at).trim() !== "") {
    const createdAt = moment(String(row.created_at).trim(), RETEST_CREATED_AT_FORMATS, true);
    if (createdAt.isValid()) {
      return createdAt;
    }
  }
  return parseRetestWorkorderDate(row.retest_workorder_id);
};

/** Stable key for retest rows with a blank / missing retest_reason. */
export const NO_RETEST_REASON_KEY = "label:No reason";
export const NO_RETEST_REASON_LABEL = "No reason";

/**
 * @param {object} row
 * @returns {{ key: string, name: string }}
 */
export const resolveRetestReasonBucket = (row = {}) => {
  if (row.retest_reason == null || String(row.retest_reason).trim() === "") {
    return {
      key: NO_RETEST_REASON_KEY,
      name: NO_RETEST_REASON_LABEL,
    };
  }
  const name = toChartLabel(row.retest_reason);
  return {
    key: buildChartSeriesKey(null, name),
    name,
  };
};

/**
 * Build business-customer_id → { customerPk, companyName } from filter customer options.
 * @param {{ value: number|string, customerId?: string, companyName?: string }[]} [customerOptions]
 * @returns {Map<string, { customerPk: number, companyName: string }>}
 */
export const buildRetestCustomerLookup = (customerOptions = []) => {
  const lookupByBusinessId = new Map();
  const safeOptions = Array.isArray(customerOptions) ? customerOptions : [];

  safeOptions.forEach((option) => {
    const businessCustomerId = option?.customerId != null && String(option.customerId).trim() !== ""
      ? String(option.customerId).trim()
      : "";
    const customerPk = toOptionalIntegerId(option?.value);
    if (!businessCustomerId || customerPk === "") {
      return;
    }
    lookupByBusinessId.set(businessCustomerId, {
      customerPk,
      companyName: String(option?.companyName ?? "").trim(),
    });
  });

  return lookupByBusinessId;
};

/**
 * Resolve customer company name for charts / Excel / modal.
 * Prefer row.customer_company, then the customers-dropdown catalog.
 * @param {object} row
 * @param {Map<string, { customerPk: number, companyName: string }>|null} [customerLookup]
 * @returns {string}
 */
export const resolveRetestCustomerCompanyName = (row = {}, customerLookup = null) => {
  if (row.customer_company != null && String(row.customer_company).trim() !== "") {
    return String(row.customer_company).trim();
  }

  const businessCustomerId = row.customer_id != null && row.customer_id !== ""
    ? String(row.customer_id)
    : "";
  const catalogName = businessCustomerId
    ? customerLookup?.get(businessCustomerId)?.companyName
    : "";
  return catalogName;
};

/**
 * Customer donut: key = business customer_id; label prefers row.customer_company, then catalog.
 * customerPk comes from the customers dropdown catalog (required for drill-down).
 * @param {object} row
 * @param {Map<string, { customerPk: number, companyName: string }>|null} [customerLookup]
 * @returns {{ key: string, name: string, customerPk: number|null }}
 */
export const resolveRetestCustomerBucket = (row = {}, customerLookup = null) => {
  const businessCustomerId = row.customer_id != null && row.customer_id !== ""
    ? String(row.customer_id)
    : "";
  const catalogEntry = businessCustomerId && customerLookup?.get(businessCustomerId);
  const companyName = resolveRetestCustomerCompanyName(row, customerLookup);
  const name = companyName || businessCustomerId || UNKNOWN_CHART_LABEL;
  const rowCustomerPk = toOptionalIntegerId(row.customer_pk);
  const customerPk = catalogEntry?.customerPk
    ?? (rowCustomerPk !== "" ? rowCustomerPk : null);

  return {
    key: buildChartSeriesKey(businessCustomerId || null, name),
    name,
    customerPk,
  };
};

/**
 * Category donut: prefer test_category_id + catalog name (export / future list fields).
 * @param {object} row
 * @param {Map<number, string>|null} [categoryLabelById]
 * @returns {{ key: string, name: string, testCategoryId: number|null }}
 */
export const resolveRetestCategoryBucket = (row = {}, categoryLabelById = null) => {
  const testCategoryId = toOptionalIntegerId(row.test_category_id);
  const hasCategoryId = testCategoryId !== "";
  const catalogName = hasCategoryId && categoryLabelById?.has(testCategoryId)
    ? categoryLabelById.get(testCategoryId)
    : null;
  const fallbackName = row.category_name ?? row.test_category_name;
  const name = catalogName != null
    ? toChartLabel(catalogName)
    : toChartLabel(fallbackName);

  return {
    key: buildChartSeriesKey(hasCategoryId ? testCategoryId : null, name),
    name,
    testCategoryId: hasCategoryId ? testCategoryId : null,
  };
};

/**
 * Sort count buckets by count (desc), then name.
 * @param {{ name: string, count: number, key?: string }[]} buckets
 * @returns {{ name: string, count: number, key?: string }[]}
 */
const sortCountBuckets = (buckets) => (
  [...buckets].sort((left, right) => {
    if (right.count !== left.count) {
      return right.count - left.count;
    }
    return left.name.localeCompare(right.name);
  })
);

/**
 * Count rows into a primary series with nested secondary counts for tooltips.
 * @param {object[]} rows
 * @param {(row: object) => { key: string, name: string }} resolvePrimaryBucket
 * @param {(row: object) => { key: string, name: string }} resolveNestedBucket
 * @param {string} nestedSeriesField
 * @returns {object[]}
 */
const buildNestedCountChartSeries = (
  rows,
  resolvePrimaryBucket,
  resolveNestedBucket,
  nestedSeriesField,
) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const bucketByKey = new Map();

  safeRows.forEach((row) => {
    const primary = resolvePrimaryBucket(row);
    const nested = resolveNestedBucket(row);

    if (!bucketByKey.has(primary.key)) {
      bucketByKey.set(primary.key, {
        key: primary.key,
        name: primary.name,
        testCategoryId: primary.testCategoryId ?? null,
        customerPk: primary.customerPk ?? null,
        count: 0,
        nestedByKey: new Map(),
      });
    }

    const primaryBucket = bucketByKey.get(primary.key);
    primaryBucket.count += 1;

    if (!primaryBucket.nestedByKey.has(nested.key)) {
      primaryBucket.nestedByKey.set(nested.key, {
        key: nested.key,
        name: nested.name,
        count: 0,
      });
    }
    primaryBucket.nestedByKey.get(nested.key).count += 1;
  });

  return Array.from(bucketByKey.values())
    .map((bucket) => {
      const { nestedByKey, ...seriesPoint } = bucket;
      return {
        ...seriesPoint,
        [nestedSeriesField]: sortCountBuckets(Array.from(nestedByKey.values())),
      };
    })
    .sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }
      return left.name.localeCompare(right.name);
    });
};

/**
 * Customer donut series from retest rows (reason breakdown in tooltip).
 * @param {object[]} rows
 * @param {Map<string, { customerPk: number, companyName: string }>|null} [customerLookup]
 * @returns {object[]}
 */
export const buildRetestCustomerChartSeries = (rows = [], customerLookup = null) => (
  buildNestedCountChartSeries(
    rows,
    (row) => resolveRetestCustomerBucket(row, customerLookup),
    resolveRetestReasonBucket,
    "reasonBreakdown",
  )
);

/**
 * Category donut series from retest rows (reason breakdown in tooltip).
 * @param {object[]} rows
 * @param {Map<number, string>|null} [categoryLabelById]
 * @returns {object[]}
 */
export const buildRetestCategoryChartSeries = (rows = [], categoryLabelById = null) => (
  buildNestedCountChartSeries(
    rows,
    (row) => resolveRetestCategoryBucket(row, categoryLabelById),
    resolveRetestReasonBucket,
    "reasonBreakdown",
  )
);

/**
 * Resolve period key / label / bounds for a row date + aggregate.
 * @param {moment.Moment} rowDate
 * @param {string} aggregate — day | week | month
 * @returns {{
 *   periodKey: string,
 *   periodLabel: string,
 *   periodStart: string,
 *   periodEnd: string,
 * }|null}
 */
const resolveRetestPeriodBucket = (rowDate, aggregate) => {
  if (!rowDate || !rowDate.isValid()) {
    return null;
  }

  if (aggregate === "month") {
    const periodStart = rowDate.clone().startOf("month");
    const periodEnd = rowDate.clone().endOf("month");
    return {
      periodKey: periodStart.format("YYYY-MM"),
      periodLabel: periodStart.format("MMM YYYY"),
      periodStart: periodStart.format("YYYY-MM-DD"),
      periodEnd: periodEnd.format("YYYY-MM-DD"),
    };
  }

  if (aggregate === "week") {
    const periodStart = rowDate.clone().startOf("week");
    const periodEnd = rowDate.clone().endOf("week");
    return {
      periodKey: periodStart.format("YYYY-MM-DD"),
      periodLabel: `${periodStart.format("MM/DD/YYYY")} – ${periodEnd.format("MM/DD/YYYY")}`,
      periodStart: periodStart.format("YYYY-MM-DD"),
      periodEnd: periodEnd.format("YYYY-MM-DD"),
    };
  }

  const periodStart = rowDate.clone().startOf("day");
  return {
    periodKey: periodStart.format("YYYY-MM-DD"),
    periodLabel: periodStart.format("MM/DD/YYYY"),
    periodStart: periodStart.format("YYYY-MM-DD"),
    periodEnd: periodStart.format("YYYY-MM-DD"),
  };
};

/**
 * Collapse reason totals to top named reasons + Other (by total volume).
 * @param {Map<string, { key: string, name: string, total: number }>} reasonTotals
 * @param {number} maxNamedLines
 * @returns {{
 *   namedReasons: Array<{ key: string, name: string }>,
 *   reasonKeyMap: Map<string, string>,
 * }}
 */
const collapseReasonSeriesKeys = (
  reasonTotals,
  maxNamedLines = MAX_NAMED_REASON_LINES,
) => {
  const sortedReasons = Array.from(reasonTotals.values()).sort((left, right) => {
    if (right.total !== left.total) {
      return right.total - left.total;
    }
    return left.name.localeCompare(right.name);
  });

  if (sortedReasons.length <= maxNamedLines) {
    const reasonKeyMap = new Map();
    sortedReasons.forEach((reason) => {
      reasonKeyMap.set(reason.key, reason.key);
    });
    return {
      namedReasons: sortedReasons.map((reason) => ({
        key: reason.key,
        name: reason.name,
      })),
      reasonKeyMap,
    };
  }

  const namedSliceCount = Math.max(1, maxNamedLines - 1);
  const namedReasons = sortedReasons.slice(0, namedSliceCount).map((reason) => ({
    key: reason.key,
    name: reason.name,
  }));
  const rolledReasons = sortedReasons.slice(namedSliceCount);
  const reasonKeyMap = new Map();

  namedReasons.forEach((reason) => {
    reasonKeyMap.set(reason.key, reason.key);
  });
  rolledReasons.forEach((reason) => {
    reasonKeyMap.set(reason.key, OTHER_CHART_SLICE_KEY);
  });

  return {
    namedReasons: [
      ...namedReasons,
      { key: OTHER_CHART_SLICE_KEY, name: OTHER_CHART_SLICE_NAME },
    ],
    reasonKeyMap,
  };
};

/**
 * Pivot retest rows into multi-line volume-over-time series by retest reason.
 * Collapses to ≤8 series (top reasons + Other) by total volume.
 *
 * @param {object[]} [rows]
 * @param {string} [aggregate] — day | week | month
 * @returns {{
 *   chartRows: object[],
 *   lineDescriptors: Array<{ dataKey: string, legendName: string, reasonIndex: number }>,
 * }}
 */
export const buildRetestReasonLineSeries = (
  rows = [],
  aggregate = DEFAULT_RETEST_AGGREGATE,
) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const reasonTotals = new Map();
  const periodByKey = new Map();

  safeRows.forEach((row) => {
    const rowDate = resolveRetestRowDate(row);
    const period = resolveRetestPeriodBucket(rowDate, aggregate);
    if (!period) {
      return;
    }

    const reason = resolveRetestReasonBucket(row);
    // Volume-by-reason chart only plots named reasons — blank retest_reason is not a series.
    if (reason.key === NO_RETEST_REASON_KEY) {
      return;
    }

    if (!reasonTotals.has(reason.key)) {
      reasonTotals.set(reason.key, {
        key: reason.key,
        name: reason.name,
        total: 0,
      });
    }
    reasonTotals.get(reason.key).total += 1;

    if (!periodByKey.has(period.periodKey)) {
      periodByKey.set(period.periodKey, {
        periodKey: period.periodKey,
        periodLabel: period.periodLabel,
        periodStart: period.periodStart,
        periodEnd: period.periodEnd,
        countsByReasonKey: new Map(),
      });
    }

    const periodBucket = periodByKey.get(period.periodKey);
    const currentCount = periodBucket.countsByReasonKey.get(reason.key) || 0;
    periodBucket.countsByReasonKey.set(reason.key, currentCount + 1);
  });

  if (periodByKey.size === 0 || reasonTotals.size === 0) {
    return {
      chartRows: [],
      lineDescriptors: [],
    };
  }

  const {
    namedReasons,
    reasonKeyMap,
  } = collapseReasonSeriesKeys(reasonTotals);

  const lineDescriptors = namedReasons.map((reason, reasonIndex) => ({
    dataKey: reason.key,
    legendName: reason.name,
    reasonIndex,
  }));

  const chartRows = Array.from(periodByKey.values())
    .sort((left, right) => left.periodKey.localeCompare(right.periodKey))
    .map((periodBucket) => {
      const chartRow = {
        periodKey: periodBucket.periodKey,
        periodLabel: periodBucket.periodLabel,
        periodStart: periodBucket.periodStart,
        periodEnd: periodBucket.periodEnd,
      };

      namedReasons.forEach((reason) => {
        chartRow[reason.key] = 0;
      });

      periodBucket.countsByReasonKey.forEach((count, reasonKey) => {
        const seriesKey = reasonKeyMap.get(reasonKey);
        if (!seriesKey) {
          return;
        }
        chartRow[seriesKey] = (chartRow[seriesKey] || 0) + count;
      });

      return chartRow;
    });

  return {
    chartRows,
    lineDescriptors,
  };
};

/**
 * Build retest_traceability scoped params for a chart drill-down selection.
 * Reason-period scopes date_range; reason text is filtered after fetch.
 */
export const buildRetestDrillDownExportParams = (filters, selection, formatDateRange) => {
  const baseParams = buildRetestTraceabilityParams(filters, formatDateRange);
  if (!baseParams || !selection) {
    return null;
  }

  if (selection.kind === RETEST_DRILL_DOWN_KIND.CATEGORY) {
    const testCategoryId = toOptionalIntegerId(selection.testCategoryId)
      || parseChartSeriesNumericId(selection.seriesKey);
    if (testCategoryId === "") {
      return null;
    }
    return { ...baseParams, test_category_id: [testCategoryId] };
  }

  if (selection.kind === RETEST_DRILL_DOWN_KIND.CUSTOMER) {
    const customerPk = toOptionalIntegerId(selection.customerPk);
    if (customerPk === "") {
      return null;
    }
    return { ...baseParams, customer_pk: customerPk };
  }

  if (selection.kind === RETEST_DRILL_DOWN_KIND.REASON_PERIOD) {
    const periodDateRange = buildLabOpsDateRangeFromPeriod(
      selection.periodStart,
      selection.periodEnd,
    );
    if (!periodDateRange) {
      return null;
    }
    return { ...baseParams, date_range: periodDateRange };
  }

  return null;
};

/**
 * Build retest_traceability scoped params for a table row click.
 * List/export expose GBID as original_wo_gbid; request query still uses genista_id.
 */
export const buildRetestRowExportParams = (filters, record, formatDateRange) => {
  const baseParams = buildRetestTraceabilityParams(filters, formatDateRange);
  const genistaId = record?.original_wo_gbid;
  if (!baseParams || !record?.retest_workorder_id || !genistaId) {
    return null;
  }

  return {
    ...baseParams,
    workorder_id: String(record.retest_workorder_id),
    genista_id: String(genistaId),
  };
};

/**
 * True when any row carries a non-empty retest_reason (list has it; export may not).
 * @param {object[]} [rows]
 * @returns {boolean}
 */
const rowsIncludeRetestReason = (rows = []) => (
  (Array.isArray(rows) ? rows : []).some(
    (row) => row?.retest_reason != null && String(row.retest_reason).trim() !== "",
  )
);

/**
 * After period-scoped export fetch, keep only the clicked reason series.
 * Export payload may omit retest_reason — in that case return the period rows as-is
 * (date_range was already narrowed for the clicked period).
 */
export const filterRetestRowsForDrillDown = (rows, selection) => {
  if (
    !selection
    || selection.kind !== RETEST_DRILL_DOWN_KIND.REASON_PERIOD
    || !selection.seriesKey
  ) {
    return rows;
  }
  if (selection.seriesKey === OTHER_CHART_SLICE_KEY) {
    return [];
  }

  const safeRows = Array.isArray(rows) ? rows : [];
  if (!rowsIncludeRetestReason(safeRows)) {
    return safeRows;
  }

  return safeRows.filter(
    (row) => resolveRetestReasonBucket(row).key === selection.seriesKey,
  );
};

/**
 * Summarize status (+ optional no-reason) counts for drill-down subtitle
 * (e.g. "Pending: 12 · Ready: 6 · No reason: 3").
 * Omits "No reason" when rows have no retest_reason field (export shape).
 * @param {object[]} [rows]
 * @returns {string}
 */
export const buildRetestStatusCountLabel = (rows = []) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  if (safeRows.length === 0) {
    return "";
  }

  const countsByStatus = new Map();
  let noReasonCount = 0;
  const includeNoReasonCount = rowsIncludeRetestReason(safeRows);

  safeRows.forEach((row) => {
    const statusLabel = row?.status != null && row.status !== ""
      ? String(row.status)
      : "Unknown";
    countsByStatus.set(statusLabel, (countsByStatus.get(statusLabel) || 0) + 1);

    if (includeNoReasonCount && resolveRetestReasonBucket(row).key === NO_RETEST_REASON_KEY) {
      noReasonCount += 1;
    }
  });

  const parts = Array.from(countsByStatus.entries())
    .sort((left, right) => left[0].localeCompare(right[0]))
    .map(([statusLabel, count]) => `${statusLabel}: ${count}`);

  if (includeNoReasonCount) {
    parts.push(`${NO_RETEST_REASON_LABEL}: ${noReasonCount}`);
  }

  return parts.join(" · ");
};

/* -------------------------------------------------------------------------- */
/* Excel export                                                               */
/* -------------------------------------------------------------------------- */

/** Export / modal column order — Customer first; catalogs fill missing API names. */
const RETEST_EXPORT_COLUMNS = [
  { key: "customer_company", header: "Customer" },
  { key: "customer_id", header: "Customer ID" },
  { key: "retest_workorder_id", header: "Retest WO" },
  { key: "original_workorder_id", header: "Original WO" },
  { key: "original_wo_gbid", header: "GBID" },
  { key: "sample_id", header: "Sample ID" },
  { key: "sample_type", header: "Sample Type" },
  { key: "test_category_name", header: "Test Category" },
  { key: "test_name", header: "Test" },
  { key: "original_result", header: "Original Result" },
  { key: "rt_result", header: "Retest Result" },
  { key: "status", header: "Status" },
  { key: "created_by_name", header: "Created By" },
  { key: "created_at", header: "Created At" },
];

const buildRetestExportRow = (row, categoryLabelById = null, customerLookup = null) => ({
  customer_company: resolveRetestCustomerCompanyName(row, customerLookup),
  customer_id: row.customer_id,
  retest_workorder_id: row.retest_workorder_id,
  original_workorder_id: row.original_workorder_id,
  original_wo_gbid: row.original_wo_gbid,
  sample_id: row.sample_id,
  sample_type: row.sample_type,
  test_category_name: resolveTestCategoryName(row.test_category_id, categoryLabelById),
  test_name: row.test_name,
  original_result: row.original_result,
  rt_result: row.rt_result,
  status: row.status,
  created_by_name: row.created_by_name,
  created_at: row.created_at,
});

export const buildRetestExportRows = (
  apiRows,
  categoryLabelById = null,
  customerLookup = null,
) => (
  Array.isArray(apiRows)
    ? apiRows.map((row) => buildRetestExportRow(row, categoryLabelById, customerLookup))
    : []
);

export const buildRetestExportFileName = (scopeLabel = "") => (
  buildDataOpsExportFileName("Retest", scopeLabel)
);

export const downloadRetestExportExcel = (rows, fileName) => {
  downloadDataOpsExcel({
    rows,
    columns: RETEST_EXPORT_COLUMNS,
    fileName,
    sheetName: "Retest",
  });
};
